/**
 * Tender Overview ("Tender Summary" user-facing) — frontend types.
 *
 * camelCase mirror of the backend shapes in
 * `backend/src/lib/extraction/tender-overview-types.ts`. `highlightData` is
 * typed as `SourceHighlightData | null` (from the assistant store) so the
 * shared `CitationLink` / source-preview helpers accept these citations
 * directly.
 *
 * Spec: Specs/tender-summary-spec.md
 */

import type { SourceHighlightData } from '@/stores/assistant-store'

// ===================
// Section keys
// ===================

export const TENDER_OVERVIEW_SECTION_KEYS = [
  'scope_of_works',
  'contract_commercial',
  'client_context',
  'red_flags',
  'respond_to',
  'client_questions',
] as const
export type TenderOverviewSectionKey = (typeof TENDER_OVERVIEW_SECTION_KEYS)[number]

// ===================
// Key-fact fields
// ===================

export const KEY_FACT_FIELDS = [
  'contracting_entity',
  'tender_reference',
  'contract_type',
  'estimated_value',
  'term',
  'location',
  'submission_deadline',
  'pricing_model',
] as const
export type KeyFactField = (typeof KEY_FACT_FIELDS)[number]

/** Anchor fields always render in the UI grid even when not stated. */
export const KEY_FACT_ANCHORS: KeyFactField[] = [
  'contracting_entity',
  'submission_deadline',
  'contract_type',
  'estimated_value',
]

// ===================
// Data shapes
// ===================

export interface ResolvedCitation {
  marker: number
  documentId: string
  documentName: string
  chunkIndex: number
  page: number | null
  quote: string
  highlightData: SourceHighlightData | null
}

export interface KeyFactValue {
  value: string | null
  citation: ResolvedCitation | null
}

export interface TenderOverviewSection {
  key: TenderOverviewSectionKey
  contentMd: string
  notAddressed: boolean
  citations: ResolvedCitation[]
}

/**
 * Scope-detection booleans emitted alongside the summary. Detection signal only:
 * no UI renders these, they exist so a mis-classified bid's scope profile can be
 * refined from the summary run.
 */
export interface TenderOverviewSignals {
  weightedCriteriaPresent: boolean
  returnablesPresent: boolean
}

export interface TenderOverview {
  id: string
  bidId: string
  keyFacts: Record<KeyFactField, KeyFactValue>
  sections: TenderOverviewSection[]
  /** null on overviews generated before the signals tag existed. */
  signals: TenderOverviewSignals | null
  model: string
  generatedAt: string
  createdAt: string
  updatedAt: string
}

// ===================
// Display labels
// ===================

/** Human-readable labels for the key-fact grid. */
export const KEY_FACT_LABELS: Record<KeyFactField, string> = {
  contracting_entity: 'Contracting entity',
  tender_reference: 'Tender reference',
  contract_type: 'Contract type',
  estimated_value: 'Estimated value',
  term: 'Term',
  location: 'Location',
  submission_deadline: 'Submission deadline',
  pricing_model: 'Pricing model',
}

/** Human-readable labels for the narrative sections. */
export const SECTION_LABELS: Record<TenderOverviewSectionKey, string> = {
  scope_of_works: 'Scope of works',
  contract_commercial: 'Contract & commercial',
  client_context: 'Client & context',
  red_flags: 'Red flags & watchouts',
  respond_to: 'What we must respond to',
  client_questions: 'Questions for the client',
}
